import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { assemblyService } from '../../services/assemblyService';
import ConnectionStatus from '../connection/ConnectionStatus';
import ChatComponent from '../chat/ChatComponent';
import QuorumStatus from '../quorum/QuorumStatus';
import VotingResults from '../voting/VotingResults';
import VotingParticipation from '../voting/VotingParticipation';
import VotingSessionManager from '../voting/VotingSessionManager';
import VotingHistory from '../voting/VotingHistory';
import AgendaManager from '../agenda/AgendaManager';
import DelegationSummaryCard from '../DelegationSummaryCard';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import LoadingState from '../common/LoadingState';
import {
  getAssemblyModalityLabel,
  getAssemblyStatusLabel,
  getAssemblyTypeLabel,
} from '../../utils/assemblyLabels';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs } from '../../utils/assemblyDate';
import {
  ChatBubbleLeftRightIcon as ChatBubbleIcon,
  UsersIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  SpeakerWaveIcon as MegaphoneIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ArrowLeftOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { Assembly, AssemblyOverviewView, QuorumManagerView } from '../../types/assembly';
import { getRoleDisplayName } from '../../context/AuthContext';

interface AssemblyDashboardProps {
  assemblyId: string;
  userRole: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
  onAssemblySelect?: (assembly: Assembly | null) => void;
}

type DashboardTabId = 'overview' | 'agenda' | 'chat' | 'voting' | 'settings';

const MIN_LOADING_MS = 250;

const ensureMinimumLoadingTime = async (startTime: number) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
  }
};

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';
const metricCardClassName = 'rounded-xl border border-slate-200 bg-slate-50/80 p-4 transition-shadow hover:shadow-sm';
const sectionTitleClassName = 'text-base md:text-lg font-semibold tracking-tight text-slate-900';
const labelClassName = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500';

const AssemblyDashboard: React.FC<AssemblyDashboardProps> = ({
  assemblyId,
  userRole,
  onAssemblySelect,
}) => {
  const location = useLocation();
  const { participantsCount, quorumStatus, joinAssembly, leaveAssembly, isConnected } = useSocket();
  const [activeTab, setActiveTab] = useState<DashboardTabId>('voting');
  const [assembly, setAssembly] = useState<AssemblyOverviewView | null>(null);
  const [isLoadingAssembly, setIsLoadingAssembly] = useState<boolean>(false);
  const [openVotingSessionId, setOpenVotingSessionId] = useState<string | null>(null);
  const [fallbackParticipantsCount, setFallbackParticipantsCount] = useState<number | null>(null);
  const [liveConnectedParticipantsCount, setLiveConnectedParticipantsCount] = useState<number | null>(null);
  const [summaryQuorumStatus, setSummaryQuorumStatus] = useState<QuorumManagerView | null>(null);
  const hasJoinedRef = useRef<string | null>(null);
  const { confirm } = useConfirm();

  const tabs: Array<{ id: DashboardTabId; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = [
    { id: 'voting', label: 'Votación', icon: ChartBarIcon },
    { id: 'overview', label: 'Resumen', icon: UsersIcon },
    { id: 'agenda', label: 'Agenda', icon: DocumentTextIcon },
    { id: 'chat', label: 'Chat', icon: ChatBubbleIcon },
    ...(userRole === 'admin' ? [{ id: 'settings' as DashboardTabId, label: 'Gestión', icon: Cog6ToothIcon }] : []),
  ];
  const tabGridClassName = tabs.length === 5
    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
    : 'grid-cols-2 sm:grid-cols-4';

  useEffect(() => {
    setFallbackParticipantsCount(participantsCount ?? 0);
  }, [participantsCount]);

  useEffect(() => {
    if (assembly?.status === 'completed') {
      setFallbackParticipantsCount(0);
    }
  }, [assembly?.status]);

  useEffect(() => {
    const targetAssemblyId = location.state?.targetAssemblyId;

    if (
      location.state?.openVotingDetails &&
      (!targetAssemblyId || targetAssemblyId === assemblyId)
    ) {
      setActiveTab('voting');
      if (location.state?.votingSessionId) {
        setOpenVotingSessionId(location.state.votingSessionId);
      }
      window.history.replaceState(null, '');
    }
  }, [assemblyId, location.state]);

  useEffect(() => {
    const loadAssembly = async () => {
      const startTime = Date.now();
      setIsLoadingAssembly(true);
      try {
        const assemblyData = await assemblyService.getAssemblyOverviewView(assemblyId);
        setAssembly(assemblyData);
        setFallbackParticipantsCount(assemblyData.connectedParticipantsCount);
        setLiveConnectedParticipantsCount(assemblyData.connectedParticipantsCount);
        setSummaryQuorumStatus(null);

        if (userRole === 'admin' && assemblyData.status === 'in_progress') {
          try {
            const summaryData = await assemblyService.getAssemblySummaryManagerView(assemblyId);
            setAssembly(summaryData);
            setFallbackParticipantsCount(summaryData.connectedParticipantsCount);
            setLiveConnectedParticipantsCount(summaryData.connectedParticipantsCount);
            setSummaryQuorumStatus(summaryData.quorum);
          } catch (error) {
            console.error('Error al cargar resumen inicial (manager-view):', error);
          }
        }
      } catch {
        setAssembly(null);
        setFallbackParticipantsCount(null);
        setLiveConnectedParticipantsCount(null);
        setSummaryQuorumStatus(null);
      } finally {
        await ensureMinimumLoadingTime(startTime);
        setIsLoadingAssembly(false);
      }
    };

    loadAssembly();

    return () => {
      hasJoinedRef.current = null;
    };
  }, [assemblyId, userRole]);

  useEffect(() => {
    if (!assemblyId || userRole !== 'admin') {
      return;
    }

    if (!assembly || assembly.status !== 'in_progress') {
      return;
    }

    let intervalId: number | null = null;

    const refreshConnectedParticipants = async () => {
      try {
        const summaryData = await assemblyService.getAssemblySummaryManagerView(assemblyId);
        setLiveConnectedParticipantsCount(summaryData.connectedParticipantsCount);
        setSummaryQuorumStatus(summaryData.quorum);
      } catch (error) {
        console.error('Error al refrescar resumen (manager-view):', error);
      }
    };

    const restartPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }

      const intervalMs = document.visibilityState === 'visible' ? 10000 : 30000;
      intervalId = window.setInterval(refreshConnectedParticipants, intervalMs);
    };

    refreshConnectedParticipants();
    restartPolling();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshConnectedParticipants();
      }
      restartPolling();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [assemblyId, userRole, assembly]);

  useEffect(() => {
    if (!assemblyId || !isConnected) {
      return;
    }

    if (hasJoinedRef.current === assemblyId) {
      return;
    }

    hasJoinedRef.current = assemblyId;

    const joinAssemblyFlow = async () => {
      try {
        await assemblyService.joinAssembly(assemblyId);
        await joinAssembly(assemblyId);
      } catch (err) {
        console.error('Error al unirse a la asamblea:', err);
        hasJoinedRef.current = null;
      }
    };

    joinAssemblyFlow();
  }, [assemblyId, isConnected, joinAssembly]);

  const handleLeaveAssembly = async () => {
    const shouldLeave = await confirm({
      title: 'Abandonar asamblea',
      message: '¿Estás seguro de que quieres abandonar esta asamblea?',
      confirmText: 'Abandonar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldLeave) return;

    try {
      await leaveAssembly(assemblyId);
      if (onAssemblySelect) {
        onAssemblySelect(null);
      }
    } catch (error) {
      console.error('Error al abandonar asamblea:', error);
    }
  };

  const renderTabContent = () => {
    if (isLoadingAssembly) {
      return <LoadingState />;
    }

    const socketPresentUsers = quorumStatus?.presentUsers;
    const connectedParticipants = userRole === 'admin'
      ? (
          liveConnectedParticipantsCount
          ?? (Number.isFinite(socketPresentUsers) ? Number(socketPresentUsers) : null)
          ?? fallbackParticipantsCount
          ?? 0
        )
      : (participantsCount ?? fallbackParticipantsCount ?? 0);

    const formattedAssemblyDate = assembly?.scheduledDate
      ? formatAssemblyCalendarDateEs(assembly.scheduledDate)
      : 'Por definir';

    const formattedAssemblyTime = formatAssemblyTimeEs(assembly?.startTime);

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {userRole === 'admin' && assembly?.status !== 'completed' && assembly?.status !== 'cancelled' && (
              <div className={`${shellCardClassName} p-5 md:p-6`}>
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h3 className={`${sectionTitleClassName} flex items-center gap-2`}>
                      <UsersIcon className="h-5 w-5 text-blue-600" />
                      Estado del Quórum
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Seguimiento en tiempo real de la participación y la representación.
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-50/70 p-3 md:p-4">
                  <QuorumStatus
                    assemblyId={assemblyId}
                    assemblyStatus={assembly?.status}
                    externalQuorumStatus={summaryQuorumStatus}
                    disableApiSync={userRole === 'admin'}
                  />
                </div>
              </div>
            )}

            {userRole === 'owner' && (
              <div className={shellCardClassName}>
                <DelegationSummaryCard assemblyId={assemblyId} />
              </div>
            )}

            <div className={`${shellCardClassName} p-5 md:p-6`}>
              <div className="mb-5 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className={`${sectionTitleClassName} flex items-center gap-2`}>
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                    Información de la Asamblea
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Resumen operativo y datos de referencia de la sesión.
                  </p>
                </div>
                <div className="inline-flex w-fit items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold tracking-wide text-blue-700">
                  {getAssemblyStatusLabel(assembly?.status)}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Título</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">
                    {assembly?.title || 'Asamblea en curso'}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Tu Rol</div>
                  <div className="mt-1 text-sm font-medium capitalize text-slate-900 md:text-base">
                    {getRoleDisplayName(userRole)}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Estado</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">
                    {getAssemblyStatusLabel(assembly?.status)}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Tipo</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">
                    {getAssemblyTypeLabel(assembly?.type)}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Modalidad</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">
                    {getAssemblyModalityLabel(assembly?.isVirtual)}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Fecha y Hora</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">{formattedAssemblyDate}</div>
                  <div className="text-sm text-slate-600">{formattedAssemblyTime}</div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Duración estimada</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">
                    {assembly?.estimatedDuration ? `${assembly.estimatedDuration} min` : 'No especificada'}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Código de acceso</div>
                  <div className="mt-1 font-mono text-sm font-semibold text-slate-900">
                    {assembly?.code || 'No disponible'}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>ID de Asamblea</div>
                  <div className="mt-1 break-all font-mono text-sm font-semibold text-slate-900">
                    {assemblyId}
                  </div>
                </div>
                <div className={metricCardClassName}>
                  <div className={labelClassName}>
                    {assembly?.isVirtual ? 'Enlace de reunión' : 'Lugar de reunión'}
                  </div>
                  {assembly?.isVirtual ? (
                    assembly?.meetingLink ? (
                      <a
                        href={assembly.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block break-all text-sm font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 transition-colors hover:text-blue-800"
                      >
                        {assembly.meetingLink}
                      </a>
                    ) : (
                      <div className="mt-1 text-sm text-slate-600">No definido</div>
                    )
                  ) : (
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {assembly?.location || 'Por definir'}
                    </div>
                  )}
                </div>
                {userRole === 'admin' ? (
                  <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm">
                    <div className={labelClassName}>Participantes Conectados</div>
                    <div className="mt-1 flex items-center gap-2 text-2xl font-bold text-emerald-700">
                      <UsersIcon className="h-5 w-5" />
                      {connectedParticipants}
                    </div>
                    <div className="mt-2 flex items-center text-xs font-medium text-emerald-700">
                      <span className="mr-1 h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                      En tiempo real
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                    <div className="text-sm font-semibold text-amber-900">Información de Participantes</div>
                    <div className="mt-1 text-xs text-amber-800">
                      El administrador comparte esta información durante la transmisión
                    </div>
                  </div>
                )}
                <div className={metricCardClassName}>
                  <div className={labelClassName}>Estado de Conexión</div>
                  <div className="mt-2">
                    <ConnectionStatus />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'agenda':
        return (
          <div className="space-y-6">
            <div className={`${shellCardClassName} overflow-hidden`}>
              <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50/90 via-sky-50/70 to-transparent px-5 py-4 md:px-6 md:py-5">
                <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-900">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                  Agenda de la Asamblea
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Consulta o gestiona los puntos programados y su estado durante la sesión.
                </p>
              </div>
              <AgendaManager
                assemblyId={assemblyId}
                userRole={userRole}
                assemblyStatus={assembly?.status as 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | undefined}
              />
            </div>
          </div>
        );

      case 'chat':
        return (
          <div className={`${shellCardClassName} flex h-[500px] flex-col overflow-hidden md:h-[600px]`}>
            <div className="flex-shrink-0 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-transparent px-4 py-3 md:px-5 md:py-4">
              <h3 className="flex items-center text-base font-semibold text-slate-900 md:text-lg">
                <ChatBubbleIcon className="mr-2 h-4 w-4 text-blue-600 md:h-5 md:w-5" />
                Chat de la Asamblea
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Conversación en tiempo real entre los participantes.
              </p>
            </div>
            <div className="min-h-0 flex-1 bg-white">
              <ChatComponent assemblyId={assemblyId} className="h-full" maxHeight="h-full" userRole={userRole} />
            </div>
          </div>
        );

      case 'voting':
        return (
          <div className="space-y-6">
            {userRole !== 'admin' && (
              <div className={shellCardClassName}>
                <div className="border-b border-slate-200 px-5 py-4">
                  <h3 className="flex items-center text-lg font-semibold text-slate-900">
                    <ChartBarIcon className="mr-2 h-5 w-5 text-blue-600" />
                    Participar en Votación
                  </h3>
                </div>
                <div className="p-5 md:p-6">
                  <VotingParticipation assemblyId={assemblyId} userRole={userRole} />
                </div>
              </div>
            )}

            {userRole === 'admin' && (
              <div className={shellCardClassName}>
                <div className="border-b border-slate-200 px-5 py-4">
                  <h3 className="flex items-center text-lg font-semibold text-slate-900">
                    <ChartBarIcon className="mr-2 h-5 w-5 text-blue-600" />
                    Resultados en Vivo
                  </h3>
                </div>
                <div className="p-5 md:p-6">
                  <VotingResults />
                </div>
              </div>
            )}

            {userRole === 'admin' && (
              <div className={shellCardClassName}>
                <div className="border-b border-slate-200 px-5 py-4">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Historial de Votaciones
                  </h3>
                </div>
                <div className="p-5 md:p-6">
                  <VotingHistory
                    assemblyId={assemblyId}
                    userRole={userRole}
                    openVotingSessionId={openVotingSessionId}
                    onVotingOpened={() => setOpenVotingSessionId(null)}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'settings':
        return userRole === 'admin' ? (
          <div className="space-y-6">
            <div className={shellCardClassName}>
              <VotingSessionManager
                assemblyId={assemblyId}
                userRole={userRole}
                assemblyStatus={assembly?.status as 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | undefined}
              />
            </div>

            <div className={shellCardClassName}>
              <div className="border-b border-slate-200 px-5 py-4">
                <h3 className="flex items-center text-lg font-semibold text-slate-900">
                  <MegaphoneIcon className="mr-2 h-5 w-5 text-blue-600" />
                  Configuración de Notificaciones
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Ajustes visuales y de experiencia para la transmisión en curso.
                </p>
              </div>
              <div className="space-y-3 p-5 md:p-6">
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
                  <span className="text-slate-700">Notificaciones de entrada de usuarios</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    defaultChecked
                  />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
                  <span className="text-slate-700">Notificaciones de votación</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    defaultChecked
                  />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
                  <span className="text-slate-700">Alertas de quórum</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    defaultChecked
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(219,234,254,0.6),_rgba(248,250,252,0.85)_38%,_#f8fafc_100%)] text-slate-900">
      <div className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/85 shadow-[0_4px_20px_-18px_rgba(15,23,42,0.7)] backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 py-3 md:py-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
                <BuildingOfficeIcon className="h-3.5 w-3.5" />
                Asamblea en vivo
              </div>
              <h1 className="mt-2 text-lg font-bold tracking-tight text-slate-900 md:text-2xl">
                Dashboard de Asamblea
              </h1>
              <p className="hidden text-xs text-slate-600 sm:block md:text-sm">
                Gestión en tiempo real de la asamblea
              </p>
            </div>
            <div className="flex shrink-0 items-center space-x-2 md:space-x-4">
              <div className="hidden sm:block">
                <ConnectionStatus />
              </div>
              {onAssemblySelect && (
                <button
                  onClick={handleLeaveAssembly}
                  className="flex items-center space-x-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 md:space-x-2 md:px-4 md:text-sm"
                  title="Abandonar Asamblea"
                >
                  <ArrowLeftOnRectangleIcon className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">Salir</span>
                </button>
              )}
            </div>
          </div>

          <div className="py-2">
            <div
              role="tablist"
              aria-label="Secciones del dashboard"
              className={`grid gap-2 px-0.5 py-1 ${tabGridClassName}`}
            >
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                const spanFullMobileRow = tabs.length % 2 !== 0 && index === tabs.length - 1;

                return (
                  <button
                    key={tab.id}
                    id={`tab-${tab.id}`}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`panel-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex w-full items-center justify-center space-x-1.5 rounded-xl border px-2.5 py-2 text-xs font-medium transition-all md:space-x-2 md:px-4 md:text-sm ${
                      spanFullMobileRow ? 'col-span-2 sm:col-span-1' : ''
                    } ${
                      activeTab === tab.id
                        ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <Icon className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-2 py-4 sm:px-4 md:py-8 lg:px-8">
        <div id={`panel-${activeTab}`} role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AssemblyDashboard;